import React, { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation, useI18next, Link } from "gatsby-plugin-react-i18next"
import { Container, Button } from "src/components/common"
import { track } from "src/utils/track"
import { scrollToAnchor } from "src/utils/scroll"
import logo from "src/assets/images/logo-medienkram-black.png"
import NavLinks from "./NavLinks"
import ToggleLanguage from "./ToggleLanguage"
import Hamburger from "./Hamburger"
import {
  Wrapper,
  Inner,
  Brand,
  DesktopNav,
  Overlay,
  MobileNav,
  MobileInner,
} from "./styles"

export const Header = () => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const { language, defaultLanguage } = useI18next()
  const prefix = language !== defaultLanguage ? `/${language}` : ""
  const navRef = useRef(null)
  const buttonRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return undefined
    const nav = navRef.current
    const focusable = () =>
      nav
        ? Array.from(nav.querySelectorAll("a[href], button:not([disabled])"))
        : []
    const onKey = event => {
      if (event.key === "Escape") {
        setOpen(false)
        if (buttonRef.current) buttonRef.current.focus()
        return
      }
      if (event.key === "Tab") {
        const items = focusable()
        if (!items.length) return
        const first = items[0]
        const last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener("keydown", onKey)
    const items = focusable()
    if (items.length) items[0].focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  const onCta = location => event => {
    track("cta_click", { location })
    close()
    scrollToAnchor(event)
  }

  return (
    <Wrapper>
      <Inner as={Container}>
        <Brand as={Link} to="/" aria-label={t("back_home")}>
          <img src={logo} width="154" height="25" alt="medienkram" />
        </Brand>
        <DesktopNav aria-label={t("nav_label")}>
          <NavLinks prefix={prefix} />
          <ToggleLanguage />
          <Button href={`${prefix}/#contact`} onClick={onCta("header")}>
            {t("nav_cta")}
          </Button>
        </DesktopNav>
        <Hamburger
          ref={buttonRef}
          open={open}
          onClick={() => setOpen(!open)}
          label={open ? t("menu_close") : t("menu_open")}
        />
      </Inner>
      <Overlay $open={open} onClick={close} aria-hidden="true" />
      <MobileNav
        id="mobile-nav"
        ref={navRef}
        aria-label={t("nav_label")}
        hidden={!open}
      >
        <MobileInner>
          <NavLinks prefix={prefix} onNavigate={close} />
          <Button
            href={`${prefix}/#contact`}
            $block
            onClick={onCta("mobile-nav")}
          >
            {t("nav_cta")}
          </Button>
          <ToggleLanguage />
        </MobileInner>
      </MobileNav>
    </Wrapper>
  )
}
